import { DOCUMENT } from '@angular/common';
import { Component, ChangeDetectionStrategy, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SmoothScrollService } from './smooth-scroll.service';
import { LanguageService, SiteLanguage } from './language.service';

gsap.registerPlugin(ScrollTrigger);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('routeStage', { static: true }) private routeStage!: ElementRef<HTMLElement>;
  public showScrollToTop = false;
  private readonly siteUrl = 'https://cecilebinsztock.com';
  private navigationSubscription?: Subscription;
  private lenis?: Lenis;
  private readonly lenisTicker = (time: number): void => {
    this.lenis?.raf(time * 1000);
  };

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly smoothScroll: SmoothScrollService,
    private readonly language: LanguageService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    this.initSmoothScroll();

    this.navigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSeo();
        this.animatePageEntrance();
      });
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
    gsap.ticker.remove(this.lenisTicker);
    this.smoothScroll.disconnect();
    this.lenis?.destroy();
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    this.showScrollToTop = window.scrollY > 520;
  }

  private initSmoothScroll(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.lenis = new Lenis({
      lerp: .14,
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: .9,
      anchors: {
        offset: -88,
        duration: .8,
      },
      prevent: node => Boolean(node.closest('.pswp')),
    });

    this.smoothScroll.connect(this.lenis);
    this.lenis.on('scroll', lenis => {
      ScrollTrigger.update();
      this.showScrollToTop = lenis.scroll > 520;
    });
    gsap.ticker.add(this.lenisTicker);
    gsap.ticker.lagSmoothing(0);
  }

  public scrollToTop(): void {
    this.smoothScroll.scrollToTop();
  }

  private updateSeo(): void {
    let route = this.activatedRoute;
    while (route.firstChild) route = route.firstChild;

    const title = route.snapshot.data['title'] ?? 'Cécile Binsztock | Artiste peintre';
    const description = route.snapshot.data['description'] ?? 'Site officiel de Cécile Binsztock, artiste peintre dans le Val-d’Oise. Découvrez ses œuvres, sa biographie et ses parutions.';
    const pageName = route.snapshot.data['pageName'] ?? 'Œuvres';
    const pageLanguage = (route.snapshot.data['lang'] ?? 'fr') as SiteLanguage;
    this.language.setLanguage(pageLanguage);
    const socialImage = `${this.siteUrl}${route.snapshot.data['image'] ?? '/assets/works/2.jpg'}`;
    const imageAlt = route.snapshot.data['imageAlt'] ?? 'Peinture de Cécile Binsztock';
    const path = this.router.url.split('?')[0].split('#')[0];
    const canonicalUrl = `${this.siteUrl}${path === '/' ? '/works' : path}`;

    this.title.setTitle(title);
    this.setMeta('name', 'description', description);
    this.setMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    this.setMeta('name', 'author', 'Cécile Binsztock');
    this.setMeta('property', 'og:title', title);
    this.setMeta('property', 'og:description', description);
    this.setMeta('property', 'og:type', 'website');
    this.setMeta('property', 'og:url', canonicalUrl);
    this.setMeta('property', 'og:image', socialImage);
    this.setMeta('property', 'og:image:secure_url', socialImage);
    this.setMeta('property', 'og:image:type', socialImage.endsWith('.PNG') ? 'image/png' : 'image/jpeg');
    this.setMeta('property', 'og:image:width', route.snapshot.data['imageWidth'] ?? '2614');
    this.setMeta('property', 'og:image:height', route.snapshot.data['imageHeight'] ?? '2584');
    this.setMeta('property', 'og:image:alt', imageAlt);
    this.setMeta('property', 'og:locale', pageLanguage === 'en' ? 'en_GB' : 'fr_FR');
    this.setMeta('property', 'og:site_name', 'Cécile Binsztock');
    this.setMeta('name', 'twitter:card', 'summary_large_image');
    this.setMeta('name', 'twitter:title', title);
    this.setMeta('name', 'twitter:description', description);
    this.setMeta('name', 'twitter:image', socialImage);
    this.setMeta('name', 'twitter:image:alt', imageAlt);
    this.updateCanonical(canonicalUrl);
    this.updateAlternate(canonicalUrl, pageLanguage);
    this.updateStructuredData(pageName, title, description, canonicalUrl, socialImage);
  }

  private animatePageEntrance(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    requestAnimationFrame(() => {
      const outlet = this.routeStage.nativeElement.querySelector('router-outlet');
      const page = outlet?.nextElementSibling;
      if (!page) return;

      gsap.fromTo(page, {
        autoAlpha: 0,
        y: 20,
      }, {
        autoAlpha: 1,
        y: 0,
        duration: .7,
        ease: 'power3.out',
        clearProps: 'transform,opacity,visibility',
      });
    });
  }

  private setMeta(attribute: 'name' | 'property', key: string, content: string): void {
    this.meta.updateTag({ [attribute]: key, content }, `${attribute}='${key}'`);
  }

  private updateCanonical(url: string): void {
    let canonical = this.document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.rel = 'canonical';
      this.document.head.appendChild(canonical);
    }
    canonical.href = url;
  }

  private updateAlternate(url: string, pageLanguage: SiteLanguage): void {
    const counterpartUrl = `${this.siteUrl}${this.language.counterpart(this.router.url)}`;
    const links = {
      fr: pageLanguage === 'fr' ? url : counterpartUrl,
      en: pageLanguage === 'en' ? url : counterpartUrl,
      'x-default': pageLanguage === 'fr' ? url : counterpartUrl,
    };
    Object.entries(links).forEach(([language, href]) => {
      let link = this.document.querySelector<HTMLLinkElement>(`link[rel='alternate'][hreflang='${language}']`);
      if (!link) {
        link = this.document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = language;
        this.document.head.appendChild(link);
      }
      link.href = href;
    });
  }

  private updateStructuredData(pageName: string, title: string, description: string, url: string, image: string): void {
    let script = this.document.querySelector<HTMLScriptElement>('#route-schema');
    if (!script) {
      script = this.document.createElement('script');
      script.id = 'route-schema';
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': this.router.url.includes('/works') ? 'CollectionPage' : 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      inLanguage: this.language.isEnglish ? 'en' : 'fr-FR',
      image,
      isPartOf: { '@id': `${this.siteUrl}/#website` },
      about: { '@id': `${this.siteUrl}/#artist` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Cécile Binsztock', item: `${this.siteUrl}${this.language.path('works')}` },
          { '@type': 'ListItem', position: 2, name: pageName, item: url },
        ],
      },
    });
  }
}
